import type { QueryClient } from '@tanstack/react-query';
import type { ComponentType, Context, PropsWithChildren, ReactNode } from 'react';

import type { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import type { NavigatePort } from '@heidi/runtime/shell';
import type {
  components,
  DateFormat,
  ModalId,
  ModalViewAction,
  RecordingStatus,
} from '@heidi/schema/ml-scribe';

import type {
  ChatItem,
  CitationRegistry,
  ClientCapabilities,
  CreateAndOpenProjectArgs,
  DesktopControlApprovalPort,
  EventType,
  EvidenceAgentImage,
  EvidenceAgentSearchPayloadV1,
  EvidenceAgentSource,
  ResolvedCitationOccurrence,
  TrackWorkDesktopNurtureBannerEvent,
  TrackWorkExperienceFeedbackActioned,
  TrackWorkHomeComposerCta,
  TrackWorkHomeComposerRoutineActioned,
  TrackWorkHomeComposerViewed,
  TrackWorkUpgradeActioned,
  Workflow,
  WorkflowAnalyticsProps,
  WorkflowInstallSource,
  WorkMode,
} from '../../contracts';
import type { ObjectWorkspaceRoute } from '../../contracts/projects/chat-home-path';
import type { LinkedContextItem } from '../../contracts/projects/linked-context';
import type { InSessionRoutineButtonLabel } from '../../contracts/routines-analytics.types';
import type { WorkflowSelection } from '../../contracts/workflows/components/workflowSelection';
import type {
  ComposerDictationMicAnalyzer,
  ComposerDictationSessionState,
} from '../composer/composer-dictation-bar';
import type { DesktopTargetSelectorState } from '../projects/components/DesktopTargetSelector.types';
import type {
  CreditAllowanceRefusalCallbacks,
  ProjectsComposerSession,
  UseStopGeneratingHotkey,
} from '../projects/hooks/useProjectsComposerChatSession';
import type { RoutineTeamsSelectionProps } from '../workflows/components/ShareWorkflowDialog';
import type { RoutineComputerList } from '../workflows/routines/RoutineComputerUseSettings';
import { bindWorkHostViews } from './work-host-view-bounds';

/** The `seen_modals` record a gate reads. */
export type WorkHomeModalView = components['schemas']['ModalView'];

/** A host view read at render. Props stay at the call site. */
type HostView = ComponentType<Record<string, unknown> & { children?: ReactNode }>;

/** A dynamic import that stays off the layout's static cone. */
type HostLoader = () => Promise<{ default: HostView }>;

/** The react-pdf pair the Library thumbnail renders through. */
export interface WorkHomePdfComponents {
  Document: ComponentType<Record<string, unknown> & { children?: ReactNode }>;
  Page: ComponentType<Record<string, unknown>>;
}

/**
 * The app inputs the Work-home surfaces take, bound once from
 * `src/composition/hosts/agents/work-home-ports-wiring.ts`.
 *
 * It is its OWN seam rather than more members on the Heidi II one because its
 * members are typed in this zone's vocabulary — the desktop-target selection
 * state and the object-workspace route both live here, and the headless seam
 * may not name a `/ui` type. Four members overlap Heidi II's and bind the same
 * app functions at the wiring.
 *
 * The wiring is imported for effect by `src/components/layout/scribe-layout.tsx`
 * rather than by the Work route: the route is a Chat file, and an import there
 * would raise the Chat ledger's `src/utils` zone count. The layout is the
 * route's own static import and is evaluated before it renders.
 */
export interface WorkHomePorts {
  // ---- Routing and composition ---------------------------------------------
  /** Opens the routine-create chat, the Work-home "create a routine" row's target. */
  useOpenRoutineCreateChat: () => () => void;
  /** The landing's desktop-target picker state, read only while it is mounted. */
  useLandingDesktopTarget: () => DesktopTargetSelectorState;

  // ---- Library documents ---------------------------------------------------
  useDocumentDownloadUrl: (documentId: string | null) => {
    data?: { url?: string } | null;
    isError: boolean;
  };
  pdf: WorkHomePdfComponents;
  /** `'pdf'` for a PDF filename; the thumbnail renders nothing otherwise. */
  getDocumentType: (filename: string) => string | undefined;

  // ---- The default-landing-page banner --------------------------------------
  useIsDefaultLandingPageSettingEnabled: () => boolean;
  useDefaultLandingPageBannerWaitMs: () => number;
  useIsClinicalRecordingActive: (includeChronicle: boolean) => boolean;
  /** The boot-landing flag as a value + setter pair over the app's atom. */
  useHasBootLandedOnHome: () => [boolean, (next: boolean) => void];
  /** The pathname the document itself was loaded at, or `null`. */
  readBootPathname: () => string | null;
  isHomeBootPath: (path: string) => boolean;
  /**
   * The user's own default-page choice: `null` when they have never chosen,
   * `undefined` while the read is pending. The value catalog stays app-side.
   */
  useQueryDefaultLandingPage: (enabled: boolean) => {
    data: string | null | undefined;
  };
  useMutateMarkModalAsViewed: () => (input: {
    modal_id: ModalId;
    increment_count: boolean;
    action?: ModalViewAction;
  }) => void;
  /** The source token is the one the settings modal already records for this banner. */
  useSettingsModal: () => {
    open(href: string, source: 'default_landing_page_banner'): void;
  };
  /** Settings → Display controls, scrolled to the Default page row. */
  settingsHref: string;
  isModalClosed: (
    modalId: ModalId,
    view: WorkHomeModalView | null | undefined
  ) => boolean;
  parseTimeStampOrNull: (time: string | null | undefined, isUtc?: boolean) => Date | null;
  trackButtonClicked: (input: {
    screen_name: ScreenName;
    action: ButtonAction;
    label: string;
  }) => void;
  trackDefaultLandingPageBannerViewed: () => void;

  // ---- The object-workspace mount decision ----------------------------------
  /** `() => useFeatureFlagEnabled(KEY)`, so the package never sees a key. */
  useAgentHomeNavFlag: () => boolean | undefined;
  useObjectWorkspaceFlag: () => boolean | undefined;
  usePosthogFlagsReady: () => boolean;
  useIsProjectsEnabled: () => boolean;
}

export type { ObjectWorkspaceRoute };

/**
 * Capability snapshot the desktop bridge returns. `platform_system` stays a
 * string here; the project assembly narrows it.
 */
export type DesktopCapabilitySnapshot = Omit<ClientCapabilities, 'platform_system'> & {
  platform_system: string;
};

/** One executor accept/busy result, with no Tauri type in the shape. */
export interface ExecutorEnsureResult {
  outcome: string;
  state?: { agentRunId: string } | null;
}

/** The desktop bridge methods the Work host calls. Extra methods on the real object stay. */
export interface WorkHostRpaBridge {
  isAvailable(): boolean;
  ensureRunExecutor(params: {
    agentRunId: string;
    baseUrl: string;
    bearerToken: string;
    presentation?: {
      projectId?: string;
      projectTitle?: string;
      returnFocusTarget?: string;
    };
    redactionValues?: string[];
  }): Promise<ExecutorEnsureResult>;
  attachExistingRunExecutor(params: {
    agentRunId: string;
    bearerToken: string;
  }): Promise<ExecutorEnsureResult | null>;
  ensureRecordingExecutor(params: {
    agentRunId: string;
    baseUrl: string;
    bearerToken: string;
    uploadContext: unknown;
  }): Promise<ExecutorEnsureResult>;
  stopRunExecutorFor(agentRunId: string): Promise<boolean>;
  stopRecordingExecutorFor(agentRunId: string): Promise<boolean>;
  desktopTargetSignProjectTurn(params: {
    projectId: string;
    turnId: string;
    targetId?: string;
    canonicalBody: string;
  }): Promise<{ instanceId: string; signature: string }>;
}

export interface WorkHostDesktopPorts {
  emitProjectAgentRegistryPoke(): Promise<unknown>;
  listenProjectAgentRegistryPoke(callback: () => void): Promise<() => void>;
  useUserWorkflows(args: { enabled?: boolean }): {
    data?: { personal?: unknown[]; team?: unknown[] };
  };
  deriveAvailableWorkflowSkillsFromSummaries(summaries: unknown[]): unknown[];
  rpaBridge: WorkHostRpaBridge;
  useSetIngestFieldCandidates(): (value: unknown) => void;
  useSetIngestNotePushHints(): (value: unknown) => void;
  useSetIngestNotePushRecorded(): (value: unknown) => void;
  useSetClearCandidateMappings(): (value: unknown) => void;
  notePushTargetsQueryKey: string;
  cancelComputerUseRun(agentRunId: string): void;
  useIsExecutorStopSignalled(childRunId: string | undefined): boolean;
  getAuthToken(): string | undefined;
  isThisComputerTarget(
    target: { instance_id: string },
    instanceId: string | null
  ): boolean;
  useNativeInstanceId(enabled: boolean): { data?: string | null };
  useDesktopTargetRoster(enabled: boolean): {
    data?: { targets?: Array<{ instance_id: string }> };
  };
  getDomains(): { baseV2Url: string };
  listenRecordingExecutorStatus(
    cb: (event: { payload: { status?: { state?: string } } }) => void
  ): Promise<() => void>;
  listenRpaExecutorStatus(
    cb: (event: { payload: { status?: { state?: string } } }) => void
  ): Promise<() => void>;
  buildRecorderUploadMetadata(baseV2Url?: string): unknown;
  useCopilotRunContextByProject(): Record<
    string,
    { sessionId?: string; patientName?: string }
  >;
  readCopilotRunContextByProject(): Record<
    string,
    { sessionId?: string; patientName?: string }
  >;
  releaseDesktopControlApprovalReservation(
    toolCallId: string,
    context?: Record<string, unknown>
  ): Promise<void>;
  releaseDesktopControlApprovalReservationsForAgentRun(
    agentRunId: string,
    context?: Record<string, unknown>
  ): Promise<void>;
  subscribeDesktopControlLease(listener: () => void): () => void;
  useDesktopPermissionGate(): {
    ensurePermissions(kind: string): Promise<boolean>;
    showComputerUseRefusal(readiness: unknown): void;
  };
  hasRpaErrorCode(error: unknown, errorCode: string): boolean;
  readComputerUseRefusal(error: unknown): unknown;
  RPA_DESKTOP_CONTROL_BLOCKED_ERROR_CODE: string;
  RpaBridgeError: new (
    message: string,
    errorCode?: string,
    details?: unknown
  ) => Error & { errorCode?: string; details?: unknown };
  deriveWorkflowDetailPresentation(input: {
    bridgeAvailable: boolean;
    isLoading: boolean;
    isError: boolean;
    parseResult: unknown;
    untitledFallback: string;
  }): unknown;
  parseWorkflow(payload: unknown): unknown;
  coerceWorkflowWirePayload(value: unknown): unknown;
  DesktopBars: ComponentType<{
    isSilent?: boolean;
    className?: string;
    size?: 'default' | 'compact';
  }>;
  SYSTEM_DEFAULT_DEVICE_MARKER: string;
  // projects artifacts
  candidateMappingKey: (
    templateId: string,
    appName: string,
    workspaceId: string | null
  ) => string;
  useCandidateMappings: () => unknown;
  useSetSectionOverride: () => (value: unknown) => void;
  useQueryComputerUseNotePushTargets: (args: {
    templateId?: string;
    appName?: string;
    workspaceId?: string;
  }) => { data?: unknown; dataUpdatedAt?: number };
  // projects components
  ParamChip: ComponentType<Record<string, unknown>>;
  TemplateParts: ComponentType<Record<string, unknown>>;
  humanizeParameterName(name: string): string;
  CloudComputerExpandButton: ComponentType<{ onClick: () => void }>;
  CloudComputerLightbox: ComponentType<Record<string, unknown>>;
  CloudComputerLiveView: ComponentType<{ className?: string }>;
  useOverlayProjectCreatedListener(): void;
  invalidateShellCachesOnEvent(queryClient: QueryClient): void;
  useProjectFileExecutorAttach(args: {
    enabled?: boolean;
    parentRunId?: string;
    parentRunTerminal?: boolean;
    discoveries: unknown;
  }): void;
  useRoutinesDesktopNurtureBannerDismissed(): [boolean, (next: boolean) => void];
  routinesDesktopNurtureBannerCountThreshold: number;
  useIsWorkDesktopNurtureBannerEnabled(): boolean;
  OverlayShimmerText: ComponentType<{ children: ReactNode; className?: string }>;
  isTauri(): boolean;
  // projects PR4a
  /** Mic-permission atom, read while the landing composer is mounted. */
  useReadyToRequestMicPermissions(): boolean;
  /** Latest native level set. Imperative: a ~30 FPS stream must not re-render. */
  readTauriAudioLevels(): readonly number[] | undefined;
  getBarHeights(frequencyData: Uint8Array): readonly number[];
  isValidDesktopInstanceId(instanceId: string): boolean;
  readNativeInstanceId(): Promise<string | null>;
  resolveDesktopTargetTurn(
    queryClient: QueryClient,
    args: { desktopTargetEnabled: boolean; override: unknown }
  ): Promise<unknown>;
  useDesktopTargetSelection(args: {
    surface: 'landing' | 'project' | 'overlay';
    desktopTargetEnabled?: boolean;
  }): DesktopTargetSelectorState;
  useAudioLevelsSubscription(active: boolean): void;
  /** Shared mic analyser. Capture builds an AudioContext, so it stays a port. */
  useAudioAnalyzer(): {
    getMicAnalyzer: () => Promise<ComposerDictationMicAnalyzer>;
    releaseMicAnalyzer: (id: string) => unknown;
  };
  cancelNativeDictation(): Promise<unknown>;
  shouldUseNativeAudio(): boolean;
  stopNativeDictation(): Promise<unknown>;
  // projects PR4b
  classifyCuaCloudProviderState(
    state: string | undefined
  ): 'running' | 'waking' | 'paused' | 'unknown';
  CUA_CLOUD_MACHINE_QUERY_KEY: readonly unknown[];
  cuaCloudClient: {
    getMachineDetails(): Promise<{
      data?: { display_name: string | null; provider_state: string };
    }>;
  };
  desktopControlApprovalPort: DesktopControlApprovalPort;
  CloudComputerInlineLiveView: ComponentType<{ childAgentRunId?: string }>;
  DesktopControlApprovalWarning: ComponentType<{ message: string; className?: string }>;
  DESKTOP_TARGETS_QUERY_KEY: readonly unknown[];
  useComputerUseQueuedTarget(args: {
    childAgentRunId: string | undefined;
    enabled: boolean;
  }): { display_name: string; busy_agent_run_id?: string | null } | null;
  ProtectedActionApproval: ComponentType<{ approval: unknown }>;
  useIsDesktopOnlineReady(): boolean;
  // routines PR4
  /** The routine "Preferred computer" choices. The roster loads only while the picker shows. */
  useRoutineComputerChoices(pickerVisible: boolean): {
    enabled: boolean;
    computers: RoutineComputerList;
    thisComputerId: string | null;
  };
}

/** `accepted: false` when the recorder rejected the press: nothing ran or was written. */
export type RecordingCommandOutcome = { accepted: boolean };

export interface WorkHostSessionPorts {
  useOfflinePendingServerDeleteIds(): string[];
  flattenSessionListPages(pages: unknown): Array<{
    session_id: string;
    session_name?: string | null;
    created_at?: string | null;
    created_time_local?: string | null;
    ehr_appt_id?: string | null;
    duration?: number | null;
    patient_profile_name?: string | null;
    patient?: { name?: string | null } | null;
    patient_profile_id?: string | null;
  }>;
  useQuerySessionsList(
    params: object,
    options?: { pollEnabled?: boolean; enabled?: boolean }
  ): { data?: { pages?: unknown[] } };
  useQueryPatientProfiles(): { data?: unknown };
  useQuerySessionsV2(enabled?: boolean): {
    data?: { pages?: Array<{ sessions?: unknown[] }> };
  };
  usePrefetchSessionById(): (sessionId: string) => void;
  useSessionWindow(args: { sessionId: string; windowIndex: number }): {
    addTabAndSetActive?: (tab: unknown) => void;
  };
  useSessionWindows(args: { sessionId: string }): unknown;
  useQuerySessionWindows(args: { sessionId: string }): {
    data: {
      sessionWindows: Array<{ activeTab?: unknown }>;
      activeWindowIndex: number;
    };
  };
  useActiveSessionId(): string | null;
  useSetActiveSessionId(): (sessionId: string | null) => void;
  useAgentRecordingSession(): [string | null, (sessionId: string | null) => void];
  useMutateUpdateSessionRecordingStatus(): {
    mutateAsync: (input: {
      recordingStatus: RecordingStatus;
      sessionId: string;
      onMutate?: () => void;
    }) => Promise<unknown>;
  };
  /**
   * Pause and stop for `sessionId`. The app accepts each press, picks the recorder
   * and tracks it; an accepted press resolves after the status write.
   */
  useRecordingCommands(sessionId: string | null): {
    startRecording: () => void;
    pause: () => Promise<RecordingCommandOutcome>;
    stop: (options: { fromPaused: boolean }) => Promise<RecordingCommandOutcome>;
  };
  useTimerSeconds(): number;
  ensureDesktopRecordingDevices(): Promise<void>;
  /**
   * Points the recorder inside it at ``sessionId``. The recorder reads its
   * session from the page, and a Work page names none, so without this a pause
   * or stop from Work never reaches the microphone. A page that names a session
   * keeps it; ``null`` overrides nothing.
   */
  RecorderSessionScope: ComponentType<PropsWithChildren<{ sessionId: string | null }>>;
  // projects artifacts
  SessionIdOverrideContext: Context<string | null>;
  EmbeddingsPanel: HostView;
  useFormDraftTitle: () => [unknown, (value: unknown) => void];
  useSetFormTemplateToEdit: () => (value: unknown) => void;
  useSaveFormTemplate: () => {
    saveFormTemplate: (input: unknown) => Promise<unknown>;
  };
  FormViewType: { readonly CREATE: string; readonly EDIT: string };
  RegulatoryGuidelineBlockModal: HostView;
  RegulatoryGuidelineWarningModal: HostView;
  useFormFieldEditorFlags: () => unknown;
  useQueryTemplateById: (
    templateId: string | null | undefined,
    generationMethod: string | null | undefined
  ) => { data?: unknown; isError?: boolean; isPending?: boolean };
  useFormDraftTitleState: () => {
    isTitleDirty: boolean;
    resetDraftTitleState: () => void;
    seedDraftTitle: (title: string) => void;
  };
  FORM_FIELD_EDITOR_PORTS: unknown;
  resolveFormTemplateUrl: (template: unknown) => string | null | undefined;
  getPdfPageDimensions: (file: unknown) => Promise<unknown>;
  PatientDocumentsSection: HostView;
  DrawerSections: HostView;
  SessionHistorySection: HostView;
  useOpenPatientFullPage: () => (patientId: string | null | undefined) => boolean;
  useMutateUpdatePatientProfile: () => {
    mutateAsync: (input: unknown) => Promise<unknown>;
  };
  usePresentationAtom: () => [unknown, (value: unknown) => void];
  useSetPresentationHistoryAtom: () => (value: unknown) => void;
  useSetPresentationOriginalAtom: () => (value: unknown) => void;
  useSetSelectedSlideIndexAtom: () => (value: unknown) => void;
  usePresentation: (id: string | null) => { isFetching: boolean };
  slideBuilderMessages: Record<string, unknown>;
  SlideEditorPanel: HostView;
  SlideUndoRedoButtons: HostView;
  useSlideToolbarActions: () => unknown;
  useSlideUndoRedoHotkeys: (undo: () => void, redo: () => void) => void;
  sessionWindowsAtomFamily: (sessionId: string) => unknown;
  SessionArtifactModalHosts: HostView;
  LinkedSessionTabsHost: HostView;
  loadSplitScreen: () => Promise<unknown>;
  reportSplitScreenPreloadFailure: (error: unknown) => void;
  focusedArtifactTab: (target: unknown) => unknown;
  toFocusedArtifactTarget: (tab: unknown) => unknown;
  SplitScreenSkeletonLoader: HostView;
  useOpenSessionPage: (sessionId: string | undefined, tab?: unknown) => () => void;
  loadV2FocusedSessionArtifactPanel: HostLoader;
  loadSessionPreviewArtifact: HostLoader;
  ClinicalTasksPanelHostProvider: HostView;
  TemplateMarkdownPreview: HostView;
  useHandleEditTemplate: () => { handleViewTemplateById: (id: string) => void };
  writeHtmlAndMarkdownToClipboard: (html: string, markdown: string) => Promise<unknown>;
  describeClipboardError: (error: unknown) => {
    name?: string;
    message?: string;
    stack?: string;
  };
  loadShareTemplateModal: HostLoader;
  GET_SESSIONS_LIST: string;
  usePatientProfileHost: () => {
    useOpenPatientFullPage: () => (patientId: string | null | undefined) => boolean;
  };
  // projects components
  useQueryDocumentTemplates(enabled?: boolean): {
    data?: readonly {
      id: string;
      name?: string | null;
      template_category?: string | null;
      form_fields?: readonly unknown[] | null;
    }[];
    isLoading: boolean;
    isError: boolean;
    isPending?: boolean;
    refetch: () => unknown;
  };
  useQueryDocumentsBySessionId(sessionId: string): {
    data?: {
      documents?:
        | readonly {
            id: string;
            name?: string | null;
            index?: number | null;
            created_at?: string | null;
            document_tab_type?: string | null;
          }[]
        | null;
    } | null;
    isLoading: boolean;
  };
  // projects PR4a
  handleSlideDeleteChunk(update: unknown): void;
  handleSlideUpdateChunk(update: unknown): Promise<unknown>;
  isEditorLive<T extends object>(
    editor: T | null | undefined,
    callSite: string
  ): editor is T;
  TabType: {
    readonly CONTEXT: string;
    readonly NOTE: string;
    readonly TRANSCRIPT: string;
    readonly DOCUMENT: string;
    readonly PRECHART: string;
    readonly LETTER: string;
  };
  useDictationStore<T>(selector: (state: ComposerDictationSessionState) => T): T;
  // projects PR4b
  DraftNoteBody: ComponentType<{
    markdown: string;
    isStreaming?: boolean;
    tintAgainst?: string;
    removal?: boolean;
  }>;
  useTemplateCreationApprovalGuard(
    surface: string
  ): (toolName: string, verdict: 'approve' | 'deny') => Promise<boolean>;
  usePendingWorkFirstTurnHandoff(options: {
    sessionId: string;
    createSessionWorkProject: (input: CreateAndOpenProjectArgs) => void;
    isSubmitting: boolean;
  }): {
    optimisticFirstTurnText: string | null;
    setOptimisticFirstTurnText: (next: string | null) => void;
    isCreatingProject: boolean;
    composerHostDraft: ProjectsComposerSession['hostDraft'];
  };
  useSessionWorkLandingResearch(options?: {
    researchVerbs?: readonly unknown[];
    onOpenResearchVerb?: (verbId: string) => void;
  }): {
    composerPrefill: string | undefined;
    landingProps: Record<string, unknown>;
  };
  useSessionWorkLandingViewModel(
    sessionId: string,
    patientProfileId: string | null | undefined,
    showsLanding: boolean
  ): {
    patient: { name?: string } | null;
    suggestedWork: unknown;
  };
}

export interface WorkHostEvidencePorts {
  getResolvedShortcutsForMarket(isUS: boolean, intl: unknown): readonly unknown[];
  buildSearchableShortcuts(
    pinned: readonly unknown[],
    market: readonly unknown[]
  ): readonly unknown[];
  useEvidenceChipsConfig(): { pinnedChips: Array<{ teamShortcutId?: string }> };
  patientSexAge(profile: unknown, intl: unknown, identity: unknown): string | null;
  cancelChatAttachmentUpload(uid: string): boolean;
  useMutateDeleteChatAttachment(): {
    mutateAsync: (attachmentId: string) => Promise<void>;
  };
  useMutateUploadChatAttachment(): {
    mutateAsync: (variables: {
      uid: string;
      file: File;
      onProgressUpdate?: (progress: number) => void;
    }) => Promise<{ id: string; url: string }>;
  };
  useChatFileUpload(options: {
    adapter?: unknown;
    enableUpload?: boolean;
    formatErrorMessage?: (errorCode: string) => string;
  }): {
    files: readonly {
      uid: string;
      name: string;
      type: string;
      file: File;
      uploadStatus: 'pending' | 'uploading' | 'completed' | 'error';
      uploadProgress?: number;
      previewUrl?: string;
      attachmentId?: string;
      error?: string;
    }[];
    addFiles: (files: File[]) => void;
    removeFile: (uid: string) => void;
    clearFiles: (deleteFromServer?: boolean) => void;
    retryUpload: (uid: string) => void;
    hasErrors: boolean;
  };
  formatChatUploadErrorMessage(intl: unknown, errorCode: string): string;
  useEvidenceIfuHref(): string | null;
  useEvidenceChatSurfaceGate(): {
    isLoading: boolean;
    isBlocked: boolean;
    showNhsContactSales: boolean;
    showNhsDisclaimer: boolean;
    showManagedTeamActivationRequest: boolean;
    isSessionlessEvidenceShown: boolean;
    isNhsUser: boolean;
  };
  // projects artifacts
  LinkedContextBadge: HostView;
  // projects components
  SearchGroupTransition: ComponentType<Record<string, unknown>>;
  ChatObjectReference: ComponentType<Record<string, unknown>>;
  RefMarkdownElement: ComponentType<Record<string, unknown>>;
  resolveExportDocumentFilename(payload: {
    filename?: string;
    title?: string;
    url?: string;
  }): string;
  // projects PR4a
  /** Write-only: the disclaimer modal opens from the child run, never reads back. */
  useSetEvidenceDisclaimerModalOpen(): (open: boolean) => void;
  ImageFileCard: HostView;
  /** Marketplace dialog. A module-scope lazy boundary, off the first load. */
  ComposerShortcutMarketplace: HostView;
  filterShortcuts(
    shortcuts: readonly {
      id: string;
      label: string;
      query: string;
      description?: string;
    }[],
    rawQuery: string
  ): readonly {
    id: string;
    label: string;
    query: string;
    description?: string;
  }[];
  useComposerShortcutsFlyout(): {
    renderShortcutsFlyout?: (actions: {
      onSend: (query: string) => void;
      close: () => void;
    }) => ReactNode;
    marketplace: ReactNode;
  };
  ChatObjectTokenHoverCard: HostView;
  /** Returns a reader. The cache and locale stay on the app side of the call. */
  useCachedReferenceLabel(): (reference: unknown) => string | undefined;
  EvidenceAnswerPortsHost: HostView;
  ChatMessageContext: Context<unknown>;
  EvidenceRegulatoryFootnoteWithIfu: HostView;
  useQueryEvidenceIntegrations(): { data?: unknown; dataUpdatedAt: number };
  getPreviewUrl(file: unknown): string | undefined;
  useChatPaste(options: {
    onFilesDetected: (files: File[]) => void;
    enabled?: boolean;
  }): { handlePaste: (event: ClipboardEvent) => boolean };
  useEvidenceAccess(): { isNhsUser: boolean; isPending: boolean };
  // projects PR4b
  useEvidenceSourceOpeners(): {
    isAuthenticatedUrl: (url: string) => boolean;
    trackSourceClick: (props: object) => void;
    openLibraryDocument: (documentId: string) => void;
    openInViewer: (source: unknown) => void;
    openInNewTab: (url: string) => void;
  };
  InSessionSourcesButton: ComponentType<{ analyticsSurface?: string }>;
  usePersistedWorkMode(): [WorkMode, (next: WorkMode) => void];
  useStartEvidenceChat(sessionId: string): (message: string) => void;
  SessionReferenceOpenContext: Context<((sessionId: string) => void) | null>;
  ChatObjectList: ComponentType<{ objects: readonly unknown[] }>;
}

export interface NangoConnectButtonPortProps {
  allowedIntegrations?: string[];
  compact?: boolean;
  testId?: string;
  disabled?: boolean;
  onConnected?: () => void;
  onFailed?: () => void;
  onBusyChange?: (busy: boolean) => void;
  children?: ReactNode;
}

export interface MergeConnectButtonPortProps {
  connector: string;
  compact?: boolean;
  disabled?: boolean;
  onConnected?: () => void;
  onFailed?: () => void;
  onBusyChange?: (busy: boolean) => void;
  children?: ReactNode;
}

export interface WorkHostIntegrationsPorts {
  usePendingLetters(sessionId: string): unknown[];
  // projects artifacts
  useLetter: <T>(select: (letter: { id?: string; name?: string } | undefined) => T) => T;
  useQueryLetters: (args?: { select?: (data: unknown) => unknown }) => { data?: unknown };
  useLetterSenderActions: () => {
    handleDownloadLetterPdf: (...args: unknown[]) => unknown;
    isDownloadingLetterPdf: boolean;
  };
  loadLetterTabPane: HostLoader;
  // projects components
  useConnectedAppsProvider(): { provider: string | null | undefined };
  MergeConnectButton: ComponentType<MergeConnectButtonPortProps>;
  NangoConnectButton: ComponentType<NangoConnectButtonPortProps>;
  getEhrProviderMetadata(provider: string): { displayName: string };
  // projects PR4a
  lettersQueryOptions(sessionId: string): {
    queryKey: readonly unknown[];
    queryFn: () => Promise<
      ReadonlyArray<{ id?: string; content?: string | null; name?: string | null }>
    >;
  };
}

export interface WorkHostRoutinesPorts {
  useWorkflows(
    query?: object,
    options?: { enabled?: boolean; staleTime?: number }
  ): {
    data?: {
      workflows?: readonly { workflow_id: string; name: string; instructions?: string }[];
    };
  };
  useReferenceableLibraryDocuments(args?: {
    query?: string;
    limit?: number;
  }): readonly LinkedContextItem[];
  // projects artifacts
  useVoiceAt: (placement: string) => boolean;
  VoiceWindow: HostView;
  RoutineOverviewPane: HostView;
  useResolvableWorkflow: (workflowId: string | undefined) => unknown;
  // projects components
  RoutineRecentRunsRail: ComponentType<{ workflowId: string }>;
  useRoutineForProject(projectId: string | undefined): {
    workflow?: { workflow_id: string } | null;
  };
  useRecentRunsRail(workflowId: string): { hasRuns: boolean; isOpen: boolean };
  useAllWorkflowRuns(): { data?: { total?: number } };
  useSessionWorkflowRuns(sessionId: string): {
    data?: { runs?: unknown[] };
    isLoading: boolean;
  };
  /** Resolves the preset setup hook; its dialog stays off the layout's static cone. */
  loadPresetSetupDialog(): Promise<{
    usePresetSetupDialog: (args?: {
      onCustomise?: (workflowId: string) => void;
      disabled?: boolean;
    }) => { openSetup: (id: string) => void; dialog: ReactNode };
  }>;
  RoutineDockCarousel: ComponentType<Record<string, unknown>>;
  WorkflowsSplitView: ComponentType<Record<string, unknown>>;
  useRunRoutineToChat(): (workflowId: string) => void;
  /** Resolves the app-wide shortcut listener hook off the layout's static cone. */
  loadWorkflowShortcutListeners(): Promise<{ useWorkflowShortcutListeners: () => void }>;
  // projects PR4a
  VoiceAssistantTrigger: HostView;
  /** The routine-run mutation. Callers hand `.mutate` to the launch operation. */
  useRunAnyWorkflow(): {
    mutate: (
      run: {
        workflowId: string;
        prompt?: string;
        attachment_ids?: string[];
        session_id?: string;
        patient_profile_id?: string;
        desktopTargetOverride?: string;
        trigger_source: string;
        trigger_type: string;
      },
      callbacks: {
        onSuccess: (result: { project_id?: string | null }) => void;
        onError: () => void;
      }
    ) => void;
  };
  // projects PR4b
  RoutineRunChatHeader: ComponentType<Record<string, unknown>>;
  CreateWorkflowApprovalCard: ComponentType<Record<string, unknown>>;
  /** Off the layout's static cone. Render under Suspense. */
  RoutineCreateGuidedChat: ComponentType<Record<string, unknown>>;
  /** Setter for the form-filling upload modal. The atom stays app-side. */
  useSetFormFillingUploadOpen(): (open: boolean) => void;
  /** Checks a combo against the user's routines and the app's built-in hotkeys. */
  detectShortcutConflict(
    combo: string,
    workflows: Pick<Workflow, 'workflow_id' | 'name' | 'shortcut'>[],
    excludeWorkflowId: string
  ): RoutineShortcutConflict | null;
  analytics: RoutinesHostAnalytics;
  /** The recorded desktop steps. Off the layout's static cone; render under Suspense. */
  RecordingWorkflowSummary: ComponentType<{ recordingId: string; routineName: string }>;
  WorkflowEditorBoldButton: ComponentType<RoutineEditorMenuButtonProps>;
  WorkflowEditorItalicButton: ComponentType<RoutineEditorMenuButtonProps>;
  WorkflowEditorBulletListButton: ComponentType<RoutineEditorMenuButtonProps>;
  useOpenRoutinesSurface(): (
    selection: WorkflowSelection | null,
    options?: { origin?: 'sidebar' | 'routines' }
  ) => void;
  /** The share dialog's team picker. Off the layout's static cone; render under Suspense. */
  TeamsSelection: ComponentType<RoutineTeamsSelectionProps>;
  EvidenceCircleLoader: ComponentType<{
    size?: number;
    spread?: number;
    formation?: 'diamond' | 'square';
    easing?:
      | 'easeInOut'
      | 'circInOut'
      | 'backInOut'
      | 'anticipate'
      | 'soft'
      | 'elastic'
      | 'snappy';
    className?: string;
  }>;
  useOpenEvidencePanel(): () => void;
  SidebarNavRowStatusIcon: ComponentType<{
    status: SidebarNavRowStatus;
    showTooltip?: boolean;
    className?: string;
  }>;
  resolveChatSidebarNavRowStatus: (state: {
    isStreaming: boolean;
    needsAttention: boolean;
    hasDraft: boolean;
    isCurrentChat: boolean;
  }) => SidebarNavRowStatus;
}

type SidebarNavRowStatus = 'rest' | 'needs-attention' | 'draft' | 'streaming';

type RoutineShortcutConflict =
  | { kind: 'workflow'; workflowName: string }
  | { kind: 'builtin' };

interface RoutineEditorMenuButtonProps {
  onClick: () => void;
  active: boolean;
}

type RoutineAnalyticsWorkflow = Pick<
  Workflow,
  'workflow_id' | 'requires_desktop' | 'source_template_id'
>;

/** The routine emitters. The event vocabulary is contract; the emit stays app-side. */
interface RoutinesHostAnalytics {
  trackWorkflowActioned(props: WorkflowAnalyticsProps): void;
  trackWorkflowInstalled(
    workflow: RoutineAnalyticsWorkflow,
    installSource: WorkflowInstallSource,
    presetId?: string,
    config?: {
      has_schedule?: boolean;
      has_event_rules?: boolean;
      event_types?: EventType[];
    }
  ): void;
  trackWorkflowCreated(
    workflow: RoutineAnalyticsWorkflow,
    config?: {
      has_schedule?: boolean;
      has_event_rules?: boolean;
      event_types?: EventType[];
      install_source?: WorkflowInstallSource;
    }
  ): void;
  trackInSessionRoutinesViewed(): void;
  trackInSessionRoutineButtonClicked: (
    label: InSessionRoutineButtonLabel,
    sessionId: string
  ) => void;
}

export interface WorkHostShellPorts {
  useIsRpaRecorderEnabled(): boolean;
  useIsWorkflowsEnabled(): boolean;
  useIsSessionRoutineRunsEnabled(): boolean;
  useIsAskHeidiSourcesShortcutsEnabled(): boolean;
  useIsChatObjectRefsEnabled(): boolean;
  isSlideBuilderEnabled(): boolean;
  isChatAttachmentsEnabled(): boolean | undefined;
  parseTimeStamp(value: string | null | undefined, isUtc?: boolean): Date | null;
  isLogoutInFlight(): boolean;
  useAreShortcutsDisabled(): boolean;
  creditAllowanceRefusal: CreditAllowanceRefusalCallbacks & {
    /** The in-memory 402 refusal, subscribed where the ghost bubble merges. */
    useValue(): { projectId: string; ghostText: string } | null;
  };
  useStopGeneratingHotkey: UseStopGeneratingHotkey;
  resolveWorkBillingAnalyticsProps(): object;
  trackWorkEvent(eventName: string, props?: object): void;
  workLaunchEventName: {
    WorkArtifactActioned: string;
    WorkMessageActioned: string;
    WorkRunCompleted: string;
    WorkMessageSubmitted: string;
    WorkApprovalActioned: string;
    WorkSurfaceActioned: string;
  };
  subscribeAuthTokenChange(cb: () => void): () => void;
  useNavigate(): NavigatePort;
  useRouteRead(): {
    isReady: boolean;
    pathname: string;
    /** The URL as shown in the browser, query and hash included. */
    asPath: string;
    query: Record<string, string | string[] | undefined>;
  };
  useReplaceRouteQuery(): (query: Record<string, string | string[]>) => void;
  useQueryKindeProperties(): { data?: { region?: string } };
  // projects artifacts
  IS_INSET_DESIGN_ENABLED: () => boolean;
  useIsPatientListEnabled: () => boolean;
  useTrackWorkArtifactCanvasActions: (projectId?: string) => void;
  trackWorkArtifactCreated: (props: {
    project_id?: string;
    agent_run_id?: string;
    artifact_type?: string;
    outcome?: 'succeeded' | 'failed';
  }) => void;
  trackWorkArtifactExported: (props: {
    project_id?: string;
    agent_run_id?: string;
    artifact_type?: string;
    outcome?: 'succeeded' | 'failed';
  }) => void;
  trackPatientRecapSourceSessionClicked: (props: {
    session_id?: string;
    patient_id?: string;
  }) => void;
  cn: (...inputs: readonly unknown[]) => string;
  reportBoundaryError: (
    error: unknown,
    report: {
      boundary: string;
      fatal?: boolean;
      recoveredByReload?: boolean;
      context?: Record<string, unknown>;
    }
  ) => void;
  ChunkErrorMessage: HostView;
  createLazyChunk: (options: {
    importer: HostLoader;
    feature: string;
    actionPrefix?: string;
    boundaryName?: string;
    chunkLogMessage: string;
    renderLogMessage: (surface: string) => string;
    Fallback: ComponentType<Record<string, unknown>>;
    reportChunkFailure?: (error: unknown) => void;
  }) => HostView;
  // projects components
  useIsAgentHomeV1Enabled(): boolean;
  useIsCountrySpecificEhrSelectionEnabled(): boolean;
  personalizationSharedMessages: {
    other: { id: string; defaultMessage?: string };
  };
  useUserCountryCode(): string | undefined;
  isProductFeedbackAvailable: boolean;
  getRegion(): string;
  useQueryUser<T>(
    select: (profile: {
      is_managed?: boolean | null;
      created_at?: string | null;
      user_ref_id?: string | null;
      session_settings?: { date_format?: DateFormat | null } | null;
    }) => T
  ): { data?: T };
  trackNurtureEvent: TrackWorkDesktopNurtureBannerEvent;
  trackUpgradeActioned: TrackWorkUpgradeActioned;
  useIsNangoIntegrationsEnabled(): boolean;
  useWhiteLabelSafe(): { isWhiteLabel: boolean };
  useIsCmdShiftHOverlayEnabled(): boolean;
  isMacOS(): boolean;
  OpenTemplateEditorBlock: ComponentType<Record<string, unknown>>;
  TemplatePinBlock: ComponentType<Record<string, unknown>>;
  WorkArtefactCreatedCard: ComponentType<Record<string, unknown>>;
  reactQueryClient: QueryClient;
  // projects PR4a
  /**
   * The discard guard's interception of `next/router`: events, back-navigation
   * and `replace`. Bound to `useRouter` itself.
   */
  useRouteInterception(): WorkRouteInterception;
  cancelNavMenuSwitchTracking(): void;
  ComposerDictateSlot: HostView;
  renderPlaceholderSlots(placeholder: string): ReactNode;
  /** Read-only: another drawer being open suppresses the disclaimer. */
  useAnyDialogDrawerOpen(): boolean;
  useIsEvidenceInlineImagesEnabled(): boolean;
  useIsEvidenceSubagentResidualCapabilitiesEnabled(): boolean;
  useIsWorkSessionArtifactV2Enabled(): boolean;
  useIsUKOrEU(): {
    isUKOrEU: boolean;
    isEU: boolean;
    isPending: boolean;
    isError: boolean;
    region?: string;
  };
  useMediaQuery(query: string): boolean;
  getPlatform(): string;
  track(event: { event_name: string; props?: object; options?: object }): void;
  /** The evidence-shortcut event name. The app vocabulary stays on the app side. */
  evidenceShortcutRunEventName: string;
  trackWorkHomeComposerCta: TrackWorkHomeComposerCta;
  trackWorkHomeComposerRoutineActioned: TrackWorkHomeComposerRoutineActioned;
  trackWorkHomeComposerViewed: TrackWorkHomeComposerViewed;
  trackProjectChatErrorOnce(props: object): void;
  isOnPrem: boolean;
  /** `NODE_ENV === 'development'`. The package cannot read `process.env`. */
  isDevelopmentBuild: boolean;
  // projects PR4b
  ExperienceFeedbackNudge: ComponentType<Record<string, unknown>>;
  mergeCreditAllowanceGhost(
    items: ChatItem[],
    refusal: { projectId: string; ghostText: string } | null,
    projectId: string
  ): ChatItem[];
  useIsProjectsContextualLoaderPhrasesEnabled(): boolean;
  useIsWorkLetterArtifactEnabled(): boolean;
  useIsDesktopTargetSelectorEnabled(): boolean;
  useIsEntitlementEnabled(entitlement: string): {
    isEnabled: boolean;
    isPending: boolean;
  };
  trackMobileHandoffCardActioned(
    action: 'viewed' | 'cta_clicked' | 'dismissed',
    props?: { project_id?: string; agent_run_id?: string }
  ): void;
  trackWorkExperienceFeedbackActioned: TrackWorkExperienceFeedbackActioned;
  /** Setter for the growth QR modal. The atom stays on the growth side. */
  useSetGetMobileAppModalOpen(): (open: boolean) => void;
  mobileAppHeaderMessages: {
    newChat: { id: string; defaultMessage?: string };
    openNavigationMenu: { id: string; defaultMessage?: string };
  };
  useIsWorkInSessionEnabled(): boolean;
  /** False until OpenFeature resolves; a gate read before then is not authoritative. */
  useIsFlagProviderReady(): boolean;
  DEFAULT_DATE_FORMAT: DateFormat;
}

/**
 * The slice of `next/router` the queued-turn discard guard intercepts.
 * One member, so the guard never imports the router.
 */
export interface WorkRouteInterception {
  asPath: string;
  locales: readonly string[] | undefined;
  push(url: string): Promise<boolean>;
  replace(url: string): Promise<boolean>;
  events: {
    on(
      event: 'routeChangeStart',
      handler: (url: string, routeProps?: { shallow: boolean }) => void
    ): void;
    on(
      event: 'beforeHistoryChange' | 'routeChangeComplete' | 'routeChangeError',
      handler: () => void
    ): void;
    off(
      event: 'routeChangeStart',
      handler: (url: string, routeProps?: { shallow: boolean }) => void
    ): void;
    off(
      event: 'beforeHistoryChange' | 'routeChangeComplete' | 'routeChangeError',
      handler: () => void
    ): void;
    emit(
      event: 'routeChangeError',
      error: Error,
      url: string,
      routeProps: { shallow: boolean }
    ): void;
  };
  beforePopState(cb: (state: { as: string }) => boolean): void;
}

/** Chat-bound modules that stay app-side. Components are module-scope lazy boundaries. */
export interface WorkHostBridgePorts {
  EvidenceTimelineBody: HostView;
  useProjectEvidenceSession(args: unknown): unknown;
  buildEvidenceSessionFromPayload(payload: unknown, toolCallId: string): unknown;
  readEvidenceRenderPayload(metadata: Record<string, unknown> | undefined): unknown;
  WorkSourcesPopover: HostView;
  stripCiteTags(text: string, opts?: { streaming?: boolean }): string;
  stripImgTags(text: string, opts?: { streaming?: boolean }): string;
  buildCiteMarkers(
    text: string,
    registry: CitationRegistry,
    baseIdx?: number,
    opts?: { streaming?: boolean }
  ): {
    fullText: string;
    citationMap: Map<number, unknown>;
    occurrences: ResolvedCitationOccurrence[];
    unresolvedTokens: string[];
  };
  buildImageMarkers(
    text: string,
    registry: ReadonlyMap<string, EvidenceAgentImage>,
    baseIdx?: number,
    opts?: { streaming?: boolean }
  ): {
    fullText: string;
    placedImageIds: Set<string>;
    placedCount: number;
    unknownImageTokens: string[];
  };
  buildReferences(occurrences: readonly ResolvedCitationOccurrence[]): unknown;
  buildCitationRegistry(
    payloads: readonly EvidenceAgentSearchPayloadV1[]
  ): CitationRegistry;
  buildImageRegistry(
    payloads: readonly EvidenceAgentSearchPayloadV1[]
  ): Map<string, EvidenceAgentImage>;
  parseEvidenceAgentSearchPayload(data: unknown): EvidenceAgentSearchPayloadV1 | null;
  toChatSource(source: EvidenceAgentSource): unknown;
  useEvidenceAnswerComponents(args: unknown): unknown;
  renderCreateTemplateToolUse(item: unknown): ReactNode;
  renderUpdateWordLibraryToolUse(item: unknown): ReactNode;
  handleEvidenceChunkStreamEvent(event: unknown): void;
  // projects PR4b
  readEvidenceSession(stepId: string): { timeline: readonly unknown[] } | undefined;
  subscribeEvidenceSessions(onChange: () => void): () => void;
}

/** The seven sub-bundles the Work host adds on top of {@link WorkHomePorts}. */
export interface WorkHostBundles {
  desktop: WorkHostDesktopPorts;
  session: WorkHostSessionPorts;
  evidence: WorkHostEvidencePorts;
  integrations: WorkHostIntegrationsPorts;
  routines: WorkHostRoutinesPorts;
  shell: WorkHostShellPorts;
  bridge: WorkHostBridgePorts;
}

export type WorkHostPorts = WorkHomePorts & WorkHostBundles;

export type WorkHostBundleName =
  | 'desktop'
  | 'session'
  | 'evidence'
  | 'integrations'
  | 'routines'
  | 'shell'
  | 'bridge';

let homePorts: WorkHomePorts | null = null;
let hostBundles: WorkHostBundles | null = null;

let homeLoader: (() => WorkHomePorts) | null = null;
let hostLoader: (() => WorkHostPorts) | null = null;

export const configureWorkHomePorts = (
  next: WorkHomePorts | (() => WorkHomePorts)
): void => {
  if (typeof next === 'function') {
    homeLoader = next;
    homePorts = null;
    return;
  }
  homeLoader = null;
  homePorts = next;
};

const toBundles = (next: WorkHostPorts): WorkHostBundles => ({
  desktop: bindWorkHostViews('desktop', next.desktop) ?? next.desktop,
  session: bindWorkHostViews('session', next.session) ?? next.session,
  evidence: bindWorkHostViews('evidence', next.evidence) ?? next.evidence,
  integrations: bindWorkHostViews('integrations', next.integrations) ?? next.integrations,
  routines: bindWorkHostViews('routines', next.routines) ?? next.routines,
  shell: bindWorkHostViews('shell', next.shell) ?? next.shell,
  bridge: bindWorkHostViews('bridge', next.bridge) ?? next.bridge,
});

/**
 * Stores the seven host sub-bundles. A {@link WorkHomePorts}-only double keeps
 * using {@link configureWorkHomePorts}; this call does not replace it.
 * Also accepts a loader, resolved once on the first read.
 */
export const configureWorkHostPorts = (
  next: WorkHostPorts | (() => WorkHostPorts)
): void => {
  if (typeof next === 'function') {
    hostLoader = next;
    hostBundles = null;
    return;
  }
  hostLoader = null;
  hostBundles = toBundles(next);
};

export const getWorkHomePorts = (): WorkHomePorts => {
  if (!homePorts && homeLoader) homePorts = homeLoader();
  if (!homePorts) {
    throw new Error(
      '[agents/work-home-ports] ports not configured — the app must load ' +
        "'@/composition/hosts/agents/work-home-ports-wiring' at bootstrap; tests must call " +
        'configureWorkHomePorts() with a double first.'
    );
  }
  return homePorts;
};

/** The named sub-bundle. Unconfigured reads name the bundle and the wiring module. */
export const getWorkHostPort = <K extends WorkHostBundleName>(
  name: K
): WorkHostBundles[K] => {
  if (!hostBundles && hostLoader) hostBundles = toBundles(hostLoader());
  const bundle = hostBundles?.[name];
  if (!bundle) {
    throw new Error(
      `[agents/work-home-ports] "${name}" not configured — import ` +
        "'@/composition/hosts/agents/work-home-ports-wiring' first"
    );
  }
  return bundle;
};

export const resetWorkHomePortsForTests = (): void => {
  homePorts = null;
  hostBundles = null;
  homeLoader = null;
  hostLoader = null;
};
